import { motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";

export default function ChatButton({ isOpen, toggleChat }) {
  return (
    <div className="fixed bottom-8 right-14 md:right-8 z-[9999] group">

      {/* =========================
          Animated Glow
      ========================== */}

      {!isOpen && (
        <motion.div
          animate={{
            scale: [1, 1.45, 1],
            opacity: [0.25, 0.75, 0.25],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 blur-3xl"
        />
      )}

      {/* =========================
          Ripple Wave 1
      ========================== */}

      {!isOpen && (
        <motion.div
          animate={{
            scale: [1, 1.8],
            opacity: [0.5, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeOut",
          }}
          className="absolute inset-0 rounded-full border-2 border-fuchsia-500"
        />
      )}

      {/* =========================
          Ripple Wave 2
      ========================== */}

      {!isOpen && (
        <motion.div
          animate={{
            scale: [1, 2.2],
            opacity: [0.35, 0],
          }}
          transition={{
            duration: 2,
            delay: 1,
            repeat: Infinity,
            ease: "easeOut",
          }}
          className="absolute inset-0 rounded-full border border-violet-500"
        />
      )}

      {/* =========================
          Rotating Gradient Ring
      ========================== */}

      <motion.div
        animate={{ rotate: 360 }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 p-[2px]"
      >
        <div className="h-full w-full rounded-full bg-transparent" />
      </motion.div>

      {/* =========================
          Orbiting Sparkles
      ========================== */}

      {!isOpen && (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -inset-5"
        >
          <Sparkles
            size={12}
            className="absolute left-1/2 top-0 -translate-x-1/2 text-fuchsia-400"
          />

          <Sparkles
            size={10}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-violet-300"
          />

          <Sparkles
            size={12}
            className="absolute left-1/2 bottom-0 -translate-x-1/2 text-pink-400"
          />

          <Sparkles
            size={10}
            className="absolute left-0 top-1/2 -translate-y-1/2 text-violet-400"
          />
        </motion.div>
      )}
            {/* =========================
          Floating Button
      ========================== */}

<motion.button
        onClick={toggleChat}
        whileHover={{
          scale: 1.12,
          y: -8,
          rotate: 8,
        }}
        whileTap={{
          scale: 0.92,
        }}
        animate={{
          y: [0, -8, 0],
          rotate: [0, 2, -2, 0],
        }}
        transition={{
          y: {
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
          },
          rotate: {
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }}
        className="relative h-16 w-16 overflow-hidden rounded-full border border-white/10 bg-gradient-to-br from-[#111827] via-[#241046] to-[#0F172A] backdrop-blur-xl flex items-center justify-center shadow-[0_20px_60px_rgba(168,85,247,0.45)]"
      >

        {/* =========================
            Animated Shine
        ========================== */}

        {!isOpen && (
          <motion.div
            animate={{
              x: ["-180%", "180%"],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          />
        )}

        {/* =========================
            Icon Glow
        ========================== */}

        {!isOpen && (
          <motion.div
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.25, 0.7, 0.25],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
            }}
            className="absolute h-10 w-10 rounded-full bg-fuchsia-500 blur-xl"
          />
        )}

        {/* =========================
            Open / Close Icon
        ========================== */}

        {isOpen ? (
          <motion.div
            initial={{
              rotate: -180,
              scale: 0,
            }}
            animate={{
              rotate: 0,
              scale: 1,
            }}
            transition={{
              duration: 0.35,
            }}
            className="relative z-10"
          >
            <X
              size={28}
              className="text-white"
            />
          </motion.div>
        ) : (
          <motion.div
            animate={{
              scale: [1, 1.25, 0.95, 1],
              rotate: [0, 12, -12, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="relative z-10"
          >
            <Sparkles
              size={28}
              className="text-white drop-shadow-[0_0_16px_rgba(255,255,255,0.9)]"
            />
          </motion.div>
        )}
      </motion.button>
     

      {/* =========================
          Floating Particles
      ========================== */}

      {!isOpen && (
        <>
          <motion.span
            animate={{
              y: [0, -18, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: 0,
            }}
            className="absolute -top-2 left-2 h-2 w-2 rounded-full bg-fuchsia-400"
          />

          <motion.span
            animate={{
              y: [0, -16, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2.3,
              repeat: Infinity,
              delay: 0.6,
            }}
            className="absolute top-1 right-3 h-1.5 w-1.5 rounded-full bg-violet-300"
          />

          <motion.span
            animate={{
              y: [0, -20, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2.6,
              repeat: Infinity,
              delay: 1.2,
            }}
            className="absolute bottom-1 left-0 h-2 w-2 rounded-full bg-pink-400"
          />
        </>
      )}
    </div>
  );
}